<script setup>
import PanelLayout from "@/layouts/PanelLayout.vue";
import {computed, onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import {getContext} from "@/services/context.js";
import {GetOrganisation} from "@/services/organisationService.js";
import {GetApp} from "@/services/appService.js";
import {GetPanel, DeletePanel} from "@/services/panelsService.js";
import {Button} from "@/components/ui/button/index.js";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card/index.js";
import ErrorAlert from "@/components/ui/error-alert.vue";

const route = useRoute();
const router = useRouter();

const organisation = ref(null);
const app = ref(null);
const panel = ref(null);
const isLoading = ref(true);
const error = ref(null);
const isDeleting = ref(false);

onMounted(async () => {
  try {
    isLoading.value = true;
    error.value = null;
    
    const context = getContext(route);
    organisation.value = await GetOrganisation(context);
    app.value = await GetApp(context);
    panel.value = await GetPanel(context);
  } catch (err) {
    console.error('Failed to load data:', err);
    error.value = 'Failed to load panel data. Please try again.';
  } finally {
    isLoading.value = false;
  }
});

async function handleDelete() {
  if (!confirm('Are you sure you want to delete this panel? This action cannot be undone.')) {
    return;
  }

  try {
    isDeleting.value = true;
    error.value = null;
    
    const context = getContext(route);
    await DeletePanel(context);
    
    // Redirect to app dashboard after successful deletion
    router.push({path: `/${route.params.organisationId}/app/${route.params.appId}`});
  } catch (err) {
    console.error('Failed to delete panel:', err);
    error.value = 'Failed to delete panel. Please try again.';
  } finally {
    isDeleting.value = false;
  }
}
</script>

<template>
  <PanelLayout :organisation="organisation" :app="app" :panel="panel">
    <div class="flex-1 space-y-4">
      <ErrorAlert v-if="error" :error="{ message: error }" />

      <Card>
        <CardHeader>
          <CardTitle>Panel Settings</CardTitle>
          <CardDescription>Manage your panel settings and preferences</CardDescription>
        </CardHeader>
        <CardContent>
          <div class="space-y-4">
            <div class="space-y-2">
              <h3 class="text-lg font-medium">Danger Zone</h3>
              <p class="text-sm text-muted-foreground">
                Once you delete a panel, there is no going back. Please be certain.
              </p>
            </div>
            <Button 
              variant="destructive" 
              @click="handleDelete"
              :disabled="isDeleting"
            >
              {{ isDeleting ? 'Deleting...' : 'Delete Panel' }}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  </PanelLayout>
</template>

<style scoped>
</style> 